import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  MessageCircle, Plus, Trash2, Send, Eye, Building2, Crown, AlertTriangle,
  CheckCircle2, Users, RefreshCw, Phone, History, XCircle, ScrollText, Mail, FileText,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const SCHEDULE_LABEL = { off: 'Off', daily: 'Daily (EOD)', monthly: 'Monthly' };
const SCHEDULE_BADGE = {
  off: 'bg-muted text-muted-foreground border-border',
  daily: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  monthly: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

export default function WhatsAppDigestsPage() {
  const [data, setData] = useState({ recipients: [], whatsapp_configured: false });
  const [linkable, setLinkable] = useState({ employees: [], partners: [], branches: [] });
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Add form state
  const [form, setForm] = useState({ link_type: 'employee', link_id: '', role: 'branch_manager', branch_id: '', schedule: 'daily', channel: 'whatsapp', attach_pdf: true });

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewScope, setPreviewScope] = useState({ branch_id: '', combined: false, period: 'daily' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([
        api.get('/whatsapp-digests/recipients'),
        api.get('/whatsapp-digests/linkable'),
      ]);
      setData(r.data);
      setLinkable(l.data);
      try {
        const lg = await api.get('/whatsapp-digests/send-log?limit=25');
        setLog(lg.data?.entries || []);
      } catch { /* log is best-effort */ }
    } catch {
      toast.error('Failed to load digest settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const people = form.link_type === 'employee' ? linkable.employees : linkable.partners;

  // What contact is required for a recipient given its channel, and is it missing?
  const contactMissing = (r) => {
    const ch = r.channel || 'whatsapp';
    const needs = [];
    if ((ch === 'whatsapp' || ch === 'both') && r.phone_missing) needs.push('phone');
    if ((ch === 'email' || ch === 'both') && r.email_missing) needs.push('email');
    return needs;
  };
  const missingCount = (data.recipients || []).filter((r) => r.link_exists !== false && contactMissing(r).length > 0).length;

  const submitAdd = async () => {
    if (!form.link_id) return toast.error('Select a person to link');
    if (form.role === 'branch_manager' && !form.branch_id) return toast.error('Pick the branch this manager receives');
    try {
      await api.post('/whatsapp-digests/recipients', {
        link_type: form.link_type,
        link_id: form.link_id,
        role: form.role,
        branch_id: form.role === 'branch_manager' ? form.branch_id : null,
        schedule: form.schedule,
        channel: form.channel,
        attach_pdf: form.attach_pdf,
        enabled: true,
      });
      toast.success('Recipient added');
      setAddOpen(false);
      setForm({ link_type: 'employee', link_id: '', role: 'branch_manager', branch_id: '', schedule: 'daily', channel: 'whatsapp', attach_pdf: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add recipient');
    }
  };

  const changeSchedule = async (rid, schedule) => {
    try {
      await api.put(`/whatsapp-digests/recipients/${rid}`, { schedule });
      setData((d) => ({ ...d, recipients: d.recipients.map((r) => r.id === rid ? { ...r, schedule } : r) }));
      toast.success('Schedule updated');
    } catch { toast.error('Update failed'); }
  };

  const changeChannel = async (rid, channel) => {
    try {
      await api.put(`/whatsapp-digests/recipients/${rid}`, { channel });
      setData((d) => ({ ...d, recipients: d.recipients.map((r) => r.id === rid ? { ...r, channel } : r) }));
      toast.success('Channel updated');
    } catch { toast.error('Update failed'); }
  };

  const toggleAttachPdf = async (rid, attach_pdf) => {
    try {
      await api.put(`/whatsapp-digests/recipients/${rid}`, { attach_pdf });
      setData((d) => ({ ...d, recipients: d.recipients.map((r) => r.id === rid ? { ...r, attach_pdf } : r) }));
    } catch { toast.error('Update failed'); }
  };

  const remove = async (rid) => {
    if (!window.confirm('Remove this digest recipient?')) return;
    try {
      await api.delete(`/whatsapp-digests/recipients/${rid}`);
      setData((d) => ({ ...d, recipients: d.recipients.filter((r) => r.id !== rid) }));
      toast.success('Removed');
    } catch { toast.error('Delete failed'); }
  };

  const sendNow = async (rid, period) => {
    setSending(true);
    try {
      const res = await api.post('/whatsapp-digests/send-now', { recipient_id: rid, period });
      const details = res.data?.details || [];
      if (res.data?.message?.startsWith('0/')) {
        toast.error(details[0] || 'Nothing sent');
      } else {
        toast.success(res.data?.message || 'Sent');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const openPreview = async (scope) => {
    setPreviewScope(scope);
    setPreviewText('Loading…');
    setPreviewOpen(true);
    try {
      const params = new URLSearchParams();
      if (scope.combined) params.set('combined', 'true');
      else if (scope.branch_id) params.set('branch_id', scope.branch_id);
      params.set('period', scope.period);
      const res = await api.get(`/whatsapp-digests/preview?${params.toString()}`);
      setPreviewText(res.data?.preview || '(empty)');
    } catch {
      setPreviewText('Failed to load preview');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="whatsapp-digests-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <MessageCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">WhatsApp Digests</h1>
              <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
                Auto-send financial digests over WhatsApp. Recipients are linked to your Employee &amp; Partner
                records, so their number stays in sync. Managers get their branch; owners get every branch + a combined report.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="digests-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-recipient-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Recipient
            </Button>
          </div>
        </div>

        {/* Config status */}
        {data.whatsapp_configured ? (
          <div className="flex items-center gap-2 text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="wa-status-ok">
            <CheckCircle2 className="w-4 h-4" /> WhatsApp (Twilio) is configured — messages will be delivered.
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="wa-status-missing">
            <AlertTriangle className="w-4 h-4" /> WhatsApp not configured. Add Twilio credentials in Settings → WhatsApp to enable sending.
          </div>
        )}

        {/* Preview toolbar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Eye className="w-4 h-4" /> Preview a digest</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2">
            <Select value={previewScope.period} onValueChange={(v) => setPreviewScope((s) => ({ ...s, period: v }))}>
              <SelectTrigger className="w-36 h-9" data-testid="preview-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily (today)</SelectItem>
                <SelectItem value="monthly">Monthly (MTD)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => openPreview({ combined: true, period: previewScope.period })} data-testid="preview-combined-btn">
              Combined
            </Button>
            {linkable.branches.map((b) => (
              <Button key={b.id} variant="outline" size="sm" onClick={() => openPreview({ branch_id: b.id, period: previewScope.period })} data-testid={`preview-branch-${b.id}`}>
                {b.name}
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Missing-contact warning */}
        {missingCount > 0 && (
          <div className="flex items-center gap-2 text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="missing-contact-banner">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span><strong>{missingCount}</strong> recipient{missingCount > 1 ? 's' : ''} won't receive their digest — missing phone/email for the selected channel. Fix the contact on their Employee/Partner record.</span>
          </div>
        )}

        {/* Recipients */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4" /> Recipients ({data.recipients.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
            ) : data.recipients.length === 0 ? (
              <div className="py-10 text-center" data-testid="no-recipients">
                <MessageCircle className="w-9 h-9 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm font-medium">No recipients yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add an employee or partner to start sending digests.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {data.recipients.map((r) => {
                  const missing = contactMissing(r);
                  const recordPath = r.link_type === 'partner' ? '/partners' : '/employees';
                  return (
                  <div key={r.id} className={`flex items-center justify-between gap-3 border rounded-lg px-3 py-2.5 flex-wrap ${missing.length && r.link_exists !== false ? 'border-amber-300 bg-amber-50/40' : ''}`} data-testid={`recipient-row-${r.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${r.role === 'owner' ? 'bg-amber-100' : 'bg-sky-100'}`}>
                        {r.role === 'owner' ? <Crown className="w-4 h-4 text-amber-600" /> : <Building2 className="w-4 h-4 text-sky-600" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate flex items-center gap-2">
                          {r.name || '(unnamed)'}
                          {!r.link_exists && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-600 border-rose-200">link removed</Badge>}
                          {missing.length > 0 && r.link_exists !== false && (
                            <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300" data-testid={`missing-badge-${r.id}`}>
                              no {missing.join(' & ')}
                            </Badge>
                          )}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground flex items-center gap-2 flex-wrap">
                          <span className="capitalize">{r.link_type}</span>
                          <span>· {r.role === 'owner' ? 'Owner / Partner (all branches + combined)' : `Branch Manager — ${r.branch_name || '?'}`}</span>
                          <span className="inline-flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {r.phone_missing ? <span className="text-rose-500">no phone</span> : r.phone}
                          </span>
                          {(r.channel === 'email' || r.channel === 'both') && (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {r.email_missing ? <span className="text-rose-500">no email</span> : r.email}
                            </span>
                          )}
                          {missing.length > 0 && r.link_exists !== false && (
                            <Link to={recordPath} className="inline-flex items-center gap-0.5 text-amber-700 font-medium hover:underline" data-testid={`fix-contact-${r.id}`}>
                              Fix on record →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={r.channel || 'whatsapp'} onValueChange={(v) => changeChannel(r.id, v)}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`channel-select-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none px-1" title="Attach PDF report">
                        <input
                          type="checkbox"
                          className="rounded border-input"
                          checked={r.attach_pdf !== false}
                          onChange={(e) => toggleAttachPdf(r.id, e.target.checked)}
                          data-testid={`attach-pdf-${r.id}`}
                        />
                        <FileText className="w-3.5 h-3.5 text-rose-600" /> PDF
                      </label>
                      <Select value={r.schedule} onValueChange={(v) => changeSchedule(r.id, v)}>
                        <SelectTrigger className={`h-8 w-32 text-xs border ${SCHEDULE_BADGE[r.schedule]}`} data-testid={`schedule-select-${r.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="daily">Daily (EOD)</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" disabled={sending} onClick={() => sendNow(r.id, r.schedule === 'monthly' ? 'monthly' : 'daily')} data-testid={`send-now-${r.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> Send Now
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-rose-600" onClick={() => remove(r.id)} data-testid={`delete-recipient-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delivery Log */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><ScrollText className="w-4 h-4" /> Delivery Log <span className="text-xs font-normal text-muted-foreground">(last {log.length})</span></CardTitle>
          </CardHeader>
          <CardContent>
            {log.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground" data-testid="no-send-log">
                <History className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                No messages sent yet. Digests &amp; day summaries will appear here.
              </div>
            ) : (
              <div className="divide-y" data-testid="send-log">
                {log.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 py-2 text-[13px]" data-testid={`send-log-${e.id}`}>
                    {e.status === 'sent'
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      : <XCircle className="w-4 h-4 text-rose-500 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{e.recipient_name || e.phone || '—'}</div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-[10px] capitalize">{(e.kind || '').replace('_', ' ')}</Badge>
                        <span>{e.phone}</span>
                        {e.status !== 'sent' && e.detail && <span className="text-rose-500 truncate">· {e.detail}</span>}
                      </div>
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-recipient-dialog">
          <DialogHeader>
            <DialogTitle>Add Digest Recipient</DialogTitle>
            <DialogDescription>Link an existing employee or partner. Their WhatsApp number comes from that record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Link to</Label>
                <Select value={form.link_type} onValueChange={(v) => setForm((f) => ({ ...f, link_type: v, link_id: '' }))}>
                  <SelectTrigger className="mt-1" data-testid="form-link-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Person</Label>
                <Select value={form.link_id} onValueChange={(v) => setForm((f) => ({ ...f, link_id: v }))}>
                  <SelectTrigger className="mt-1" data-testid="form-person"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {people.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No {form.link_type}s found</div>}
                    {people.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}{p.phone ? '' : ' (no phone)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="mt-1" data-testid="form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch_manager">Branch Manager (their branch)</SelectItem>
                    <SelectItem value="owner">Owner / Partner (all + combined)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Schedule</Label>
                <Select value={form.schedule} onValueChange={(v) => setForm((f) => ({ ...f, schedule: v }))}>
                  <SelectTrigger className="mt-1" data-testid="form-schedule"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off (manual only)</SelectItem>
                    <SelectItem value="daily">Daily (end of day)</SelectItem>
                    <SelectItem value="monthly">Monthly (1st)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role === 'branch_manager' && (
              <div>
                <Label className="text-xs">Branch</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}>
                  <SelectTrigger className="mt-1" data-testid="form-branch"><SelectValue placeholder="Select branch…" /></SelectTrigger>
                  <SelectContent>
                    {linkable.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Delivery channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}>
                  <SelectTrigger className="mt-1" data-testid="form-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="both">WhatsApp + Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-foreground cursor-pointer select-none" data-testid="form-attach-pdf-label">
                  <input
                    type="checkbox"
                    className="rounded border-input"
                    checked={form.attach_pdf}
                    onChange={(e) => setForm((f) => ({ ...f, attach_pdf: e.target.checked }))}
                    data-testid="form-attach-pdf"
                  />
                  <FileText className="w-3.5 h-3.5 text-rose-600" /> Attach PDF report
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submitAdd} data-testid="form-submit">Add Recipient</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Digest Preview</DialogTitle>
            <DialogDescription>This is what the WhatsApp message will contain.</DialogDescription>
          </DialogHeader>
          <pre className="whitespace-pre-wrap text-[12.5px] leading-relaxed bg-muted/40 border rounded-lg p-3 max-h-[55dvh] overflow-auto font-mono" data-testid="preview-text">
            {previewText}
          </pre>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
